/**
 * Paddle Billing overlay (Paddle.js v2), imported lazily when the plan page mounts.
 *
 * When settings.paddle.clientToken is still a placeholder the module runs in
 * simulation mode: the overlay is skipped and the plan page walks through
 * processing → success exactly as it does with a real sandbox token. That is
 * what makes the funnel runnable end-to-end before Paddle credentials exist.
 */
import type { Settings } from './types.ts';

export type PaddleEventName = 'checkout.loaded' | 'checkout.completed' | 'checkout.closed' | 'checkout.error' | 'checkout.payment.failed';

export interface CheckoutRequest {
  priceId: string;
  email: string;
  customData: Record<string, unknown>;
  lang: string;
  /**
   * Class name of an element to render the checkout into. Paddle's "overlay"
   * mode is a full-screen takeover; rendering inline into our own dialog is
   * what keeps the plan page visible behind it.
   */
  inlineTarget?: string;
}

export interface PaddleHandlers {
  onLoaded: () => void;
  onCompleted: (transactionId: string | null) => void;
  onClosed: () => void;
  onError: (code: string) => void;
  /** the bank declined; the form stays open with Paddle's message at its top */
  onPaymentFailed?: () => void;
}

/** true only on a developer machine with no Paddle token configured */
export const isSimulated = (settings: Settings): boolean => {
  const unconfigured = !settings.paddle.clientToken || settings.paddle.clientToken.startsWith('PLACEHOLDER');
  if (!unconfigured) return false;
  const local = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
  return import.meta.env.DEV || local;
};

export class CheckoutUnavailable extends Error {
  constructor() {
    super('paddle-not-configured');
    this.name = 'CheckoutUnavailable';
  }
}

interface PaddleInstance {
  Checkout: { open: (opts: Record<string, unknown>) => void };
}

let instance: PaddleInstance | null = null;

export async function openCheckout(settings: Settings, req: CheckoutRequest, handlers: PaddleHandlers): Promise<void> {
  if (isSimulated(settings)) {
    console.warn('[paddle] no clientToken — simulating the checkout (local development only)');
    setTimeout(() => handlers.onLoaded(), 400);
    setTimeout(() => handlers.onCompleted(`sim_${Date.now()}`), 1800);
    return;
  }

  // On a real host with no token there is nothing to open. Never fake a
  // purchase here: it would show a celebration and hand the visitor to the app
  // as if they had paid.
  if (!settings.paddle.clientToken || settings.paddle.clientToken.startsWith('PLACEHOLDER')) {
    throw new CheckoutUnavailable();
  }

  const { initializePaddle } = await import('@paddle/paddle-js');
  if (!instance) {
    const paddle = await initializePaddle({
      environment: settings.paddle.env,
      token: settings.paddle.clientToken,
      eventCallback: (e: { name?: string; data?: { transaction_id?: string }; error?: { code?: string } }) => {
        switch (e.name as PaddleEventName) {
          case 'checkout.loaded':
            handlers.onLoaded();
            break;
          case 'checkout.completed':
            handlers.onCompleted(e.data?.transaction_id ?? null);
            break;
          case 'checkout.closed':
            handlers.onClosed();
            break;
          case 'checkout.error':
            handlers.onError(e.error?.code ?? 'unknown');
            break;
          case 'checkout.payment.failed':
            handlers.onPaymentFailed?.();
            break;
        }
      },
    });
    if (!paddle) throw new Error('paddle-init-failed');
    instance = paddle as unknown as PaddleInstance;
  }

  instance.Checkout.open({
    items: [{ priceId: req.priceId, quantity: 1 }],
    // Paddle rejects an empty email outright ("customer ID or email is
    // required"); leaving it out lets the overlay ask for one instead.
    ...(req.email ? { customer: { email: req.email } } : {}),
    customData: req.customData,
    settings: {
      ...(req.inlineTarget
        ? {
            displayMode: 'inline',
            frameTarget: req.inlineTarget,
            frameInitialHeight: 480,
            frameStyle: 'width:100%; min-width:312px; background-color:transparent; border:none;',
          }
        : { displayMode: 'overlay' }),
      theme: 'light',
      locale: req.lang,
      allowLogout: false,
      showAddDiscounts: settings.features.discountCodes !== false,
      variant: 'one-page',
    },
  });
}
